import { defineFeature, defineModule, definePermission } from '@shared/contract'

// Stammdaten: Artikel mit Gebinden, Barcodes, Bildern, Lieferanten und Disposition je Standort; Kunden und
// Lieferanten; Standorte und Lagerplätze; Warengruppen, Einheiten, Steuern, Zahlungsbedingungen, Gründe.
//
// Rechte – so fein, wie die Rollenmatrix sie braucht (db/seeds/demo/roles.ts vergibt 'masterdata.item.*' usw.):
//   * Lesen und Schreiben je Gegenstand getrennt.
//   * Artikel, Kunden und Lieferanten gehören dem ganzen Betrieb: Schreibrechte gelten mandantenweit
//     (locationScoped: false). `masterdata.item.read` ist standortbezogen – es bestimmt, für welche Standorte
//     jemand Bestand und Disposition eines Artikels sieht.
//   * Was an einem Standort hängt (Disposition, Lagerplätze), lässt sich je Standort vergeben.
//   * Einkaufspreise sieht nur, wer zusätzlich `costs.read` hält (Feldrecht der Plattform).
//   * Grunddaten (Einheiten, Steuern, Zahlungsbedingungen, Gründe, Warengruppen) liest jede angemeldete Person –
//     jede Auswahlliste braucht sie; ändern darf sie nur, wer das jeweilige Recht hält.
const ITEMS = 'Artikel'
const PARTNERS = 'Kunden und Lieferanten'
const LOCATIONS = 'Standorte'
const SETTINGS = 'Grunddaten'

export default defineModule({
  id: 'masterdata',
  title: 'Stammdaten',
  description:
    'Artikel mit Gebinden, Barcodes, Lieferanten und Disposition, Kunden, Lieferanten, Standorte und Grunddaten.',
  dependsOn: ['platform'],
  // Schalter der Grunddaten (db/seeds/base.ts), die die Stammdaten im Server durchsetzen: server/features.ts
  features: [
    defineFeature({
      key: 'agecheck',
      label: 'Altersprüfung',
      description: 'Pflichtbestätigung bei Artikeln mit Mindestalter an Kasse und Übergabe.',
      defaultEnabled: false,
      effect:
        'Eingeschaltet tragen Artikel eine Altersgrenze (16 oder 18 Jahre); Kasse und Übergabe verlangen dafür die Bestätigung des Alters. Ausgeschaltet lehnt der Server Altersgrenzen und Getränke über 0,5 % vol ab; ausschalten geht nur, solange kein Artikel eine Altersgrenze trägt.',
    }),
    defineFeature({
      key: 'kegs',
      label: 'Fassware',
      description: 'Fässer, Zapfanlagen und Kohlensäure als Pfand- und Leihgebinde.',
      defaultEnabled: false,
      effect:
        'Eingeschaltet lassen sich Gebinde „Fass“ mit Pfand belegen und Pfandarten der Art „Leihgebinde“ (Fass, CO₂-Flasche) anlegen. Ausgeschaltet lehnt der Server beides ab; ausschalten geht nur, solange keines davon in Gebrauch ist. Vorhandene Belege und Konten bleiben unverändert.',
    }),
  ],
  permissions: [
    definePermission({
      key: 'masterdata.item.read',
      label: 'Artikel lesen',
      group: ITEMS,
      description: 'Artikel, Gebinde, Barcodes und Bilder sehen – Bestand und Disposition nur an den eigenen Standorten.',
    }),
    definePermission({
      key: 'masterdata.item.write',
      label: 'Artikel anlegen und ändern',
      group: ITEMS,
      description: 'Stammdaten, Gebinde, Barcodes und Bilder pflegen, Artikel sperren und wieder freigeben, importieren.',
      locationScoped: false,
    }),
    definePermission({
      key: 'masterdata.item.purchasing',
      label: 'Lieferanten und Einkaufspreise der Artikel pflegen',
      group: ITEMS,
      description: 'Lieferanten zuordnen, Bestelleinheit und datierte Einkaufspreise setzen (zusätzlich „Einstandspreise sehen“).',
      locationScoped: false,
    }),
    definePermission({
      key: 'masterdata.item.planning',
      label: 'Disposition je Standort pflegen',
      group: ITEMS,
      description: 'Listung, Mindest-, Melde- und Zielbestand sowie Standardplatz – je Standort vergebbar.',
    }),
    definePermission({
      key: 'masterdata.item.groups',
      label: 'Warengruppen pflegen',
      group: ITEMS,
      locationScoped: false,
    }),

    definePermission({ key: 'masterdata.customer.read', label: 'Kunden lesen', group: PARTNERS, locationScoped: false }),
    definePermission({
      key: 'masterdata.customer.write',
      label: 'Kunden anlegen und ändern',
      group: PARTNERS,
      description: 'Stammdaten, Anschriften, Ansprechpartner und Konditionen pflegen, Kunden sperren, importieren.',
      locationScoped: false,
    }),
    definePermission({
      key: 'masterdata.customer.credit',
      label: 'Kreditlimit und Liefersperre von Kunden setzen',
      group: PARTNERS,
      locationScoped: false,
    }),
    definePermission({
      key: 'masterdata.customer.account',
      label: 'Kontostand von Kunden sehen',
      group: PARTNERS,
      description: 'Offene Posten, Überfälliges, Ausschöpfung des Kreditlimits und weitere Konten des Kunden.',
      locationScoped: false,
    }),
    definePermission({ key: 'masterdata.supplier.read', label: 'Lieferanten lesen', group: PARTNERS, locationScoped: false }),
    definePermission({
      key: 'masterdata.supplier.write',
      label: 'Lieferanten anlegen und ändern',
      group: PARTNERS,
      description: 'Stammdaten, Anschriften, Ansprechpartner und Lieferkonditionen pflegen, importieren.',
      locationScoped: false,
    }),
    definePermission({
      key: 'masterdata.supplier.account',
      label: 'Kontostand von Lieferanten sehen',
      group: PARTNERS,
      description: 'Offene Verbindlichkeiten und weitere Konten beim Lieferanten.',
      locationScoped: false,
    }),

    definePermission({
      key: 'masterdata.location.read',
      label: 'Standorte und Lagerplätze lesen',
      group: LOCATIONS,
      description: 'Sichtbar sind die Standorte, für die das Recht gilt.',
    }),
    definePermission({
      key: 'masterdata.location.write',
      label: 'Standorte anlegen und ändern',
      group: LOCATIONS,
      locationScoped: false,
    }),
    definePermission({
      key: 'masterdata.location.bins',
      label: 'Lagerplätze pflegen',
      group: LOCATIONS,
      description: 'Lagerplätze eines Standorts anlegen, umbenennen und stilllegen – je Standort vergebbar.',
    }),

    definePermission({ key: 'masterdata.unit.write', label: 'Einheiten pflegen', group: SETTINGS, locationScoped: false }),
    definePermission({
      key: 'masterdata.tax.write',
      label: 'Steuerschlüssel und Steuersätze pflegen',
      group: SETTINGS,
      locationScoped: false,
    }),
    definePermission({
      key: 'masterdata.paymentTerm.write',
      label: 'Zahlungsbedingungen pflegen',
      group: SETTINGS,
      locationScoped: false,
    }),
    definePermission({ key: 'masterdata.reasonCode.write', label: 'Gründe pflegen', group: SETTINGS, locationScoped: false }),
  ],
})
